// src/pages/HomePage.js
"use client";
import { Helmet } from "react-helmet-async";

import React from "react";

/* =========================
   Fonts
   (Add once in public/index.html or main layout head)
   <link rel="preconnect" href="https://fonts.googleapis.com">
   <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
   <link href="https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&display=swap" rel="stylesheet">
   In your global CSS:
   .heading-serif { font-family:"Instrument Serif", serif; font-weight:700; letter-spacing:-0.01em; }
   ========================= */

/* ====== Config: Google Sheets CSV URL ====== */
const SHEET_CSV_URL =
  "https://docs.google.com/spreadsheets/d/e/2PACX-1vT-YQUJcltTZ6EKNk_-LJQCFY2k2292go-9xDiLKupi4UrktEwxWtpYwv9iHEn4SmUBWxVcbdc-bdRn/pub?output=csv";

/* ====== CSV → JSON (handles quoted commas) ====== */
function splitCsv(line) {
  const out = [];
  let cur = "";
  let q = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      if (q && line[i + 1] === '"') {
        cur += '"';
        i++;
      } else {
        q = !q;
      }
    } else if (ch === "," && !q) {
      out.push(cur);
      cur = "";
    } else {
      cur += ch;
    }
  }
  out.push(cur);
  return out;
}
function csvToJson(csv) {
  const lines = csv.trim().split(/\r?\n/);
  if (!lines.length) return [];
  const headers = splitCsv(lines[0]).map((h) => h.trim());
  return lines
    .slice(1)
    .filter(Boolean)
    .map((l) => {
      const cells = splitCsv(l).map((c) => c.trim());
      const row = {};
      headers.forEach((h, i) => (row[h] = cells[i] || ""));
      return row;
    });
}
const dateNum = (d) => Date.parse(d || "") || 0;

/* ====== Layout wrappers ====== */
const Wrap = ({ children }) => (
  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">{children}</div>
);
const Section = ({ children, className = "" }) => (
  <section className={`py-12 sm:py-14 lg:py-20 ${className}`}>{children}</section>
);

/* ====== Section header (eyebrow + title + intro) ====== */
const SectionHeader = ({ eyebrow, title, intro, align = "left" }) => (
  <div className={align === "center" ? "text-center mx-auto max-w-3xl" : "max-w-3xl"}>
    {eyebrow && (
      <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-blue-700">
        {eyebrow}
      </p>
    )}
    <h2 className="heading-serif text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 mt-2">
      {title}
    </h2>
    {intro && (
      <p className="text-slate-600 text-sm sm:text-base mt-3 leading-relaxed">{intro}</p>
    )}
  </div>
);

/* ====== UI atoms ====== */
const Button = ({ children, className = "", as = "a", href = "#", onClick }) => {
  const base =
    "inline-flex items-center justify-center h-11 px-5 rounded-xl text-sm font-semibold transition " +
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600/60 focus-visible:ring-offset-2";
  const primary = `${base} bg-blue-600 text-white hover:bg-blue-700`;
  if (as === "button") {
    return (
      <button onClick={onClick} className={`${primary} ${className}`}>
        {children}
      </button>
    );
  }
  return (
    <a href={href} className={`${primary} ${className}`}>
      {children}
    </a>
  );
};

const GhostButton = ({ children, href = "#", className = "" }) => (
  <a
    href={href}
    className={`inline-flex items-center justify-center h-11 px-5 rounded-xl border border-slate-300 text-sm font-semibold text-slate-800 hover:bg-slate-50 transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600/60 focus-visible:ring-offset-2 ${className}`}
  >
    {children}
  </a>
);

const Badge = ({ children, tone = "slate", className = "" }) => {
  const tones = {
    slate: "border-slate-200 bg-slate-50 text-slate-700",
    blue: "border-blue-200 bg-blue-50 text-blue-700",
    green: "border-emerald-200 bg-emerald-50 text-emerald-700",
    gold: "border-[#e4d3b5] bg-[#faf6ee] text-[#8a6d3f]",
  };
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[10px] font-medium border ${tones[tone]} ${className}`}
    >
      {children}
    </span>
  );
};

/* ====== Service card: eyebrow tag, title, description, bullets, CTA link ====== */
const ServiceCard = ({ tag, title, text, bullets = [], href = "/contact", cta = "Learn more" }) => (
  <div className="group flex flex-col rounded-2xl border border-slate-200 bg-white shadow-sm hover:shadow-lg hover:-translate-y-1 hover:border-blue-200 transition p-6 sm:p-7">
    {tag && (
      <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-blue-700">
        {tag}
      </p>
    )}
    <h3 className="heading-serif text-xl font-semibold text-slate-900 mt-2">{title}</h3>
    {text && <p className="text-slate-600 text-sm mt-2 leading-relaxed">{text}</p>}
    {bullets.length > 0 && (
      <ul className="mt-4 space-y-2">
        {bullets.map((b, i) => (
          <li key={i} className="flex gap-2 text-sm text-slate-700">
            <span className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-blue-600" />
            <span>{b}</span>
          </li>
        ))}
      </ul>
    )}
    <a
      href={href}
      className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-blue-700 group-hover:gap-2 transition-all"
    >
      {cta} <span aria-hidden>→</span>
    </a>
  </div>
);

const SimpleCard = ({ title, text }) => (
  <div className="rounded-2xl border border-slate-200 bg-white shadow-sm hover:shadow-md hover:-translate-y-0.5 transition p-6">
    <h3 className="heading-serif text-lg font-semibold text-slate-900">{title}</h3>
    {text && <p className="text-slate-600 text-sm mt-2 leading-relaxed">{text}</p>}
  </div>
);

const MetricTile = ({ label, value }) => (
  <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-6 text-center">
    <div className="heading-serif text-2xl sm:text-3xl font-bold text-blue-700">{value}</div>
    <div className="mt-1.5 text-sm text-slate-600">{label}</div>
  </div>
);

const OpportunityCard = ({ imageUrl, title, location, tags = [], blurb }) => (
  <div className="flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm hover:shadow-lg hover:-translate-y-1 transition">
    <div className="overflow-hidden">
      <img
        src={
          imageUrl ||
          "https://images.unsplash.com/photo-1512917774080-9991f1c4c750?q=80&w=1200&auto=format&fit=crop"
        }
        alt={title}
        className="w-full aspect-[4/3] object-cover hover:scale-105 transition duration-500"
        loading="lazy"
        decoding="async"
      />
    </div>
    <div className="p-4 flex flex-col gap-2 grow">
      <div className="flex items-start justify-between gap-2">
        <h3 className="heading-serif text-base font-semibold text-slate-900 leading-snug">
          {title}
        </h3>
        {location && <Badge>{location}</Badge>}
      </div>
      {tags.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {tags.slice(0, 4).map((t, i) => (
            <Badge key={i} tone="blue">
              {t}
            </Badge>
          ))}
        </div>
      )}
      {blurb && <p className="text-sm text-slate-600">{blurb}</p>}
      <Button href="/listings" className="mt-auto">
        View listing
      </Button>
    </div>
  </div>
);

/* ====== Page ====== */
const HomePage = () => {
  const [latest4, setLatest4] = React.useState([]);
  const [loadingLatest, setLoadingLatest] = React.useState(true);

  React.useEffect(() => {
    let on = true;
    setLoadingLatest(true);
    fetch(SHEET_CSV_URL)
      .then((r) => r.text())
      .then((txt) => {
        if (!on) return;
        const rows = csvToJson(txt).map((r) => ({
          title: r.title || r.Title || "",
          type: r.type || r.Type || "",
          location: r.location || r.Location || "",
          province: r.province || r.Province || "",
          price: r.price || r.Price || "",
          image_url: r.image_url || r.Image || r.ImageURL || "",
          tags: r.tags || r.Tags || "",
          posted_at: r.posted_at || r.Posted || r.PostedAt || "",
        }));
        rows.sort((a, b) => dateNum(b.posted_at) - dateNum(a.posted_at));
        setLatest4(rows.slice(0, 4));
        setLoadingLatest(false);
      })
      .catch(() => setLoadingLatest(false));
    return () => {
      on = false;
    };
  }, []);

  return (
    <div
      className="relative flex min-h-screen flex-col bg-white overflow-x-hidden"
      style={{ fontFamily: 'Manrope, "Noto Sans", sans-serif' }}
    >
      <Helmet>
        <title>
          Real Estate Advisory & Consulting for Tech and Financial Industries | Maoq Digital Inc.
        </title>
        <meta
          name="description"
          content="Canada-first advisory and consulting. Real estate advisory for hotels, plazas, and land; consulting for tech and financial industries; lending advisory; and corporate, business & commercial banking investigations."
        />
        <link rel="canonical" href="https://www.maoqdigital.com/" />

        <meta property="og:type" content="website" />
        <meta
          property="og:title"
          content="Maoq Digital Inc. — Advisory & Consulting for Real Estate, Tech and Finance"
        />
        <meta
          property="og:description"
          content="Real Estate Advisory • Tech & Financial Consulting • Lending • Banking Investigations — Canada-first. Clean info, private process, global capital."
        />
        <meta property="og:url" content="https://www.maoqdigital.com/" />
        <meta
          property="og:image"
          content="https://www.maoqdigital.com/images/linkpreview.png"
        />
        <meta
          property="og:image:alt"
          content="Advisory and consulting for real estate, technology, and financial industries across Canada"
        />

        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Maoq Digital Inc. — Advisory & Consulting for Real Estate, Tech and Finance"
        />
        <meta
          name="twitter:description"
          content="Real Estate Advisory • Tech & Financial Consulting • Lending • Banking Investigations — Canada-first."
        />
        <meta
          name="twitter:image"
          content="https://www.maoqdigital.com/images/linkpreview.png"
        />

        {/* JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ProfessionalService",
            name: "Maoq Digital Inc.",
            url: "https://www.maoqdigital.com/",
            areaServed: "CA",
            description:
              "Canada-first advisory and consulting: real estate advisory (hotels, plazas, land), consulting for tech and financial industries, lending advisory, and corporate, business & commercial banking investigations.",
            knowsAbout: [
              "Real estate advisory",
              "Commercial real estate",
              "Technology consulting",
              "Financial services consulting",
              "Lending advisory",
              "Corporate banking investigations",
              "Commercial banking investigations",
            ],
          })}
        </script>
      </Helmet>

      <div className="flex flex-col grow h-full">
        <Wrap>
          {/* ================= HERO ================= */}
          <Section className="pt-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch">
              {/* Left: Copy block */}
              <div className="rounded-3xl border border-slate-200 bg-white shadow-sm p-7 sm:p-10 flex flex-col justify-center">
                <Badge tone="green" className="w-max">
                  Canada • Advisory & Consulting
                </Badge>
                <h1 className="heading-serif text-4xl md:text-5xl font-bold text-slate-900 mt-4 leading-[1.08]">
                  Advisory for real assets. Consulting for tech and finance.
                </h1>
                <p className="text-slate-600 text-sm sm:text-base mt-4 max-w-xl leading-relaxed">
                  We help owners and investors buy and sell hotels, plazas, and land —
                  and we advise clients in the technology and financial industries on
                  lending, and corporate, business, and commercial banking
                  investigations. Clear numbers, tidy files, real outcomes.
                </p>
                <div className="flex flex-wrap gap-3 mt-8">
                  <Button href="/listings" className="bg-black hover:bg-gray-800">
                    View all listings
                  </Button>
                  <GhostButton href="/contact">Book a consultation</GhostButton>
                  <Button
                    href="mailto:info@maoqdigital.com"
                    className="bg-[#C6A16B] hover:bg-[#b38f59] text-black"
                  >
                    Email us
                  </Button>
                </div>
                <div className="mt-7 flex flex-wrap gap-2">
                  <Badge>Real Estate Advisory</Badge>
                  <Badge>Tech Consulting</Badge>
                  <Badge>Financial Consulting</Badge>
                  <Badge>Lending</Badge>
                  <Badge>Banking Investigations</Badge>
                </div>
              </div>

              {/* Right: Image tile */}
              <div className="rounded-3xl border border-slate-200 bg-white shadow-sm overflow-hidden">
                <div className="relative h-[320px] sm:h-[380px] lg:h-full">
                  <img
                    src="https://images.unsplash.com/photo-1486325212027-8081e485255e?ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8NHx8dG9yb250b3xlbnwwfHwwfHx8MA%3D%3D&auto=format&fit=crop&q=60&w=800"
                    alt="Commercial real estate and financial district in Canada"
                    className="absolute inset-0 h-full w-full object-cover"
                    loading="lazy"
                    decoding="async"
                  />
                  <div className="absolute bottom-0 left-0 right-0 p-5 sm:p-6 bg-gradient-to-t from-black/50 to-transparent">
                    <div className="flex items-center gap-2">
                      <Badge tone="blue">Canada</Badge>
                      <span className="text-white text-xs sm:text-sm">
                        Toronto • Vancouver • Calgary • GTA
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Section>

          {/* ================= QUICK METRICS ================= */}
          <Section className="pt-2 pb-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <MetricTile
                label="Illustrative advised across principals"
                value="$250M+"
              />
              <MetricTile label="Primary markets" value="Coast-to-Coast CA" />
              <MetricTile
                label="Practice areas"
                value="Real Estate • Tech • Finance"
              />
            </div>
          </Section>

          {/* ================= SERVICES ================= */}
          <Section>
            <SectionHeader
              eyebrow="What we do"
              title="Three practices, one standard"
              intro="Whether it's a property, a technology decision, or a lending file, every engagement gets the same treatment: verified information, a clear process, and a defined outcome."
            />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-8">
              <ServiceCard
                tag="Practice 01"
                title="Real estate advisory"
                text="Buy-side and sell-side advisory for income-producing assets across Canada."
                bullets={[
                  "Hotels, plazas, and development land",
                  "Teasers, targeted buyer lists, NDAs, tours, offers",
                  "Off-market leads and clean deal files (NOI, cap rate, rent roll)",
                ]}
                href="/listings"
                cta="See current listings"
              />
              <ServiceCard
                tag="Practice 02"
                title="Tech & financial industry consulting"
                text="Practical consulting for technology companies and financial institutions."
                bullets={[
                  "Digital infrastructure, systems, and process modernization",
                  "Technology diligence on transactions and operating businesses",
                  "Advisory for lenders, funds, and financial services firms",
                ]}
                cta="Discuss your project"
              />
              <ServiceCard
                tag="Practice 03"
                title="Lending & banking investigations"
                text="Support for lending files and reviews across corporate, business, and commercial banking."
                bullets={[
                  "Lending advisory: structure, documentation, and file readiness",
                  "Review of records, transactions, and account activity",
                  "Findings reports with clear remediation steps",
                ]}
                cta="Request a review"
              />
            </div>
          </Section>

          {/* ================= WHO WE SERVE ================= */}
          <Section className="pt-0">
            <SectionHeader
              eyebrow="Who we serve"
              title="Built for owners, investors, and institutions"
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-8">
              <SimpleCard
                title="Property owners"
                text="Sell hotels, plazas, or land with a private, well-run process."
              />
              <SimpleCard
                title="Investors & buyers"
                text="Fresh listings and off-market opportunities with clean numbers."
              />
              <SimpleCard
                title="Tech companies"
                text="Consulting on infrastructure, systems, and transaction diligence."
              />
              <SimpleCard
                title="Lenders & institutions"
                text="Lending advisory and support on corporate, business, and commercial banking reviews."
              />
            </div>
          </Section>

          {/* ================= HOW WE WORK ================= */}
          <Section className="pt-0">
            <SectionHeader eyebrow="Process" title="How we work" />
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mt-8">
              {[
                ["Intro call", "Understand the asset, file, or problem."],
                ["Scope & prep", "Gather documents and set the plan."],
                ["Execute", "Reach the market or run the analysis."],
                ["Review", "Offers, findings, or recommendations."],
                ["Close out", "Complete the deal or deliver the report."],
              ].map(([t, d], i) => (
                <div
                  key={i}
                  className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm hover:shadow-md transition"
                >
                  <p className="text-[11px] font-semibold text-blue-700 mb-1">
                    Step {i + 1}
                  </p>
                  <h4 className="heading-serif text-base font-semibold text-slate-900">
                    {t}
                  </h4>
                  <p className="text-xs text-slate-600 mt-1.5 leading-relaxed">{d}</p>
                </div>
              ))}
            </div>
          </Section>

          {/* ================= WHY US ================= */}
          <Section className="pt-0">
            <SectionHeader eyebrow="Why us" title="Why clients work with us" />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-8">
              <SimpleCard
                title="Cross-industry depth"
                text="Real estate, technology, and finance under one roof — useful when your file touches more than one."
              />
              <SimpleCard
                title="Clean information"
                text="Straight numbers, verified records, and tidy data rooms so decisions are quick and defensible."
              />
              <SimpleCard
                title="Clear steps"
                text="You always know where the engagement stands and exactly what happens next."
              />
            </div>
          </Section>

          {/* ================= LATEST OPPORTUNITIES ================= */}
          <Section className="pt-0">
            <div className="flex items-end justify-between gap-4">
              <SectionHeader
                eyebrow="Real estate"
                title="Latest opportunities"
              />
              <a
                href="/listings"
                className="text-sm font-semibold text-blue-700 hover:underline whitespace-nowrap"
              >
                View all →
              </a>
            </div>

            {loadingLatest ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-8">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div
                    key={i}
                    className="aspect-[4/3] rounded-2xl bg-slate-100 animate-pulse"
                  />
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-8">
                {latest4.map((op, i) => (
                  <OpportunityCard
                    key={`${op.title}-${i}`}
                    imageUrl={op.image_url}
                    title={op.title}
                    location={op.location || op.province}
                    tags={(op.tags || "")
                      .split(",")
                      .map((s) => s.trim())
                      .filter(Boolean)
                      .slice(0, 3)}
                    blurb=""
                  />
                ))}
              </div>
            )}

            <p className="text-[11px] text-slate-500 mt-3">
              For full details and more opportunities, see the listings page.
            </p>
          </Section>

          {/* ================= CLOSING CTA ================= */}
          <Section className="pt-0 pb-16">
            <div className="rounded-3xl bg-slate-900 text-white p-8 sm:p-12 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
              <div className="max-w-2xl">
                <h2 className="heading-serif text-2xl sm:text-3xl font-bold">
                  Have a property, project, or file to discuss?
                </h2>
                <p className="text-slate-300 text-sm sm:text-base mt-2 leading-relaxed">
                  Tell us what you're working on — a hotel or plaza to sell, a tech
                  or lending question, or a banking review — and we'll come back
                  with a clear next step.
                </p>
              </div>
              <div className="flex flex-wrap gap-3">
                <Button href="/contact" className="bg-[#C6A16B] text-black hover:bg-slate-100">
                  Book a consultation
                </Button>
                <Button
                  href="mailto:info@maoqdigital.com"
                  className="bg-[#C6A16B] hover:bg-[#b38f59] text-black"
                >
                  Email us
                </Button>
              </div>
            </div>
          </Section>
        </Wrap>
      </div>
    </div>
  );
};

export default HomePage;